import { adminUsersSchema } from "@rosc/contracts";
import { Badge } from "@rosc/ui/components/badge";
import { Button } from "@rosc/ui/components/button";
import { Input } from "@rosc/ui/components/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from "@rosc/ui/components/pagination";
import { Skeleton } from "@rosc/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@rosc/ui/components/table";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { adminKeys } from "@/lib/admin-queries";
import { referenceClient } from "@/lib/orpc/client";
import { useAuthStore } from "@/store/auth.store";

export function AdminUsersTable() {
  const uid = useAuthStore((state) => state.user?.uid);
  const [search, setSearch] = useState("");
  const [email, setEmail] = useState("");
  const [cursors, setCursors] = useState<string[]>([]);
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      if (search !== email) {
        setEmail(search);
        setCursors([]);
      }
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [search, email]);
  const filters = {
    email: email || undefined,
    cursor: cursors.at(-1),
    limit: 25,
  };
  const query = useQuery({
    queryKey: adminKeys(uid ?? "").users(filters),
    queryFn: async ({ signal }) =>
      adminUsersSchema.parse(
        await referenceClient.adminUsers(
          { query: filters },
          { signal, context: { expectedUserUid: uid } },
        ),
      ),
    enabled: !!uid,
    retry: false,
  });
  return (
    <section className="space-y-4" aria-labelledby="admin-users-title">
      <h2 id="admin-users-title" className="text-xl font-semibold">
        Users
      </h2>
      <Input
        type="search"
        aria-label="Search by email"
        placeholder="Search by email prefix"
        maxLength={255}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="max-w-sm"
      />
      {query.isPending ? (
        <Skeleton className="h-64 w-full" aria-label="Loading admin users" />
      ) : query.isError ? (
        <div role="alert">
          Unable to load admin users.{" "}
          <Button onClick={() => void query.refetch()}>Try again</Button>
        </div>
      ) : query.data.items.length === 0 ? (
        <p role="status">No users found.</p>
      ) : (
        <Table>
          <TableCaption>
            Newest accounts first. Usage covers the last 30 days. Subscription
            stored status may briefly lag Stripe.
          </TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Signup date</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Subscription stored status</TableHead>
              <TableHead>Recording minutes, 30 days</TableHead>
              <TableHead>AI cost USD, 30 days</TableHead>
              <TableHead>Account badges</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.data.items.map((account) => (
              <TableRow key={account.publicId}>
                <TableCell>
                  <Link
                    to="/admin/users/$publicId"
                    params={{ publicId: account.publicId }}
                    className="underline underline-offset-4"
                  >
                    {account.email}
                  </Link>
                </TableCell>
                <TableCell>{account.createdAt ?? "Unknown"}</TableCell>
                <TableCell>{account.planName ?? "Unknown"}</TableCell>
                <TableCell>{account.planStatus ?? "Unknown"}</TableCell>
                <TableCell>
                  {account.minutes30d.toLocaleString(undefined, {
                    maximumFractionDigits: 1,
                  })}
                </TableCell>
                <TableCell>
                  {account.aiCostUsd30d.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </TableCell>
                <TableCell className="space-x-1">
                  {account.isAdmin && <Badge>Admin</Badge>}
                  {account.banned && (
                    <Badge variant="destructive">Banned</Badge>
                  )}
                  {account.deletionRequested && (
                    <Badge variant="outline">Deletion requested</Badge>
                  )}
                  {!account.hasLogin && (
                    <Badge variant="secondary">Not on the new login yet</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Pagination aria-label="Users pages">
        <PaginationContent>
          <PaginationItem>
            <Button
              variant="outline"
              disabled={cursors.length === 0 || query.isFetching}
              onClick={() => setCursors((previous) => previous.slice(0, -1))}
            >
              Previous
            </Button>
          </PaginationItem>
          <PaginationItem>
            <span className="px-3" aria-live="polite">
              Page {cursors.length + 1}
            </span>
          </PaginationItem>
          <PaginationItem>
            <Button
              variant="outline"
              disabled={
                query.isError || query.isFetching || !query.data?.nextCursor
              }
              onClick={() => {
                const next = query.data?.nextCursor;
                if (next) setCursors((previous) => [...previous, next]);
              }}
            >
              Next
            </Button>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </section>
  );
}
